import type { DatPasteRow, LoadContact } from './api'

/**
 * The companies a DAT paste names, for the capture's broker question (Stage 2, 2.2): every
 * load is filed under a broker from the book, and the book is asked once per company, with
 * every contact its rows carry — a rep one row lists can settle the company for all of them.
 */
export interface PasteCompany {
  /** The company as the screen groups it: DAT's spelling, case and spacing folded. */
  key: string
  /** As the first of its rows spells it. */
  company: string
  /** Every contact its rows carry, in the order DAT listed them, each once. */
  contacts: LoadContact[]
  /** The rows that name it, by number. */
  rows: number[]
}

/** The broker a company's loads are filed under — one the book offered, or one chosen from it. */
export interface ChosenBroker {
  id: string
  name: string
}

/**
 * What the book is asked about one company: its name and every contact its rows carry. An
 * answer — or a choice — belongs to the question it was given on: once a correction changes
 * the contacts, it is about a company nobody has asked about yet (Copilot on #40).
 */
export function questionOf(company: Pick<PasteCompany, 'company' | 'contacts'>): string {
  return JSON.stringify([company.company, company.contacts])
}

/**
 * A company as DAT spells it, case and spacing folded — the reader's own fold, so the rows
 * the server merged and the rows this screen groups agree on what one company is.
 */
export function companyKey(company: string): string {
  return company.trim().split(/\s+/).join(' ').toUpperCase()
}

/** The contacts DAT listed for a row's load: its own, then the ones its merged rows carried. */
export function contactsOf(row: Pick<DatPasteRow, 'contactEmail' | 'contactPhone' | 'otherContacts'>): LoadContact[] {
  const own = row.contactEmail === null && row.contactPhone === null ? [] : [{ email: row.contactEmail, phone: row.contactPhone }]
  return [...own, ...row.otherContacts]
}

/** The companies the rows name, in the order they first appear. Rows naming none are left out. */
export function companiesOf(rows: DatPasteRow[]): PasteCompany[] {
  const companies = new Map<string, PasteCompany>()

  for (const row of rows) {
    if (row.company === null) {
      continue
    }

    const key = companyKey(row.company)
    const company = companies.get(key) ?? { key, company: row.company, contacts: [], rows: [] }

    for (const contact of contactsOf(row)) {
      if (!company.contacts.some((known) => sameContact(known, contact))) {
        company.contacts.push(contact)
      }
    }

    company.rows.push(row.number)
    companies.set(key, company)
  }

  return [...companies.values()]
}

function sameContact(one: LoadContact, other: LoadContact): boolean {
  return (one.email ?? '').toLowerCase() === (other.email ?? '').toLowerCase() && (one.phone ?? '') === (other.phone ?? '')
}
